import React from "react";
import { useLocation, Link } from "react-router-dom";
import styled from "styled-components";

import Menu from "../../../templates/menu-template";
import Icons from "../../../components/icons-helper";
import { theme } from "../../../styles/theme";

// Itens principais do menu (sem os 3 de NFSe que agora ficam agrupados)
const itens: any = [
  {
    key: 1,
    icon: "home",
    title: "Home",
    route: ["/admin/home"],
  },
  {
    key: 2,
    icon: "user",
    title: "Novas Solicitações",
    route: ["/admin/solicitations"],
  },
  {
    key: 3,
    icon: "users",
    title: "Empresas",
    route: ["/admin/developers"],
  },
  {
    key: 5,
    icon: "admin",
    title: "Admin",
    route: ["/admin/super-dev/list"],
  },
];

// Subitens do grupo "Configurações NFSe"
const nfseSubItems = [
  {
    key: "nfse-1",
    title: "Códigos de Tributação",
    route: "/admin/taxation-code",
  },
  {
    key: "nfse-2",
    title: "Adapter Dinâmico",
    route: "/admin/city-adapter-config",
  },
  {
    key: "nfse-3",
    title: "NBS × IBSCBS",
    route: "/admin/nbs-ibscbs",
  },
  {
    key: "nfse-4",
    title: "Regras Tributárias",
    route: "/admin/regras-tributarias",
  },
];

// --- Styled Components para o accordion ---
const GroupContainer = styled.div`
  width: 100%;
  margin-left: 7px;
`;

const GroupHeader = styled.button<{ $open: boolean; $hasActive: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  cursor: pointer;
  height: 45px;
  border: none;
  border-radius: 10px;
  padding: 0 10px;
  font-family: ${theme.font.poppins};
  font-size: 0.9em;
  background-color: ${({ $hasActive }) => ($hasActive ? "rgba(12, 116, 136, 0.15)" : "transparent")};
  color: ${({ $hasActive }) => ($hasActive ? theme.colors.primary : theme.colors.blue400)};
  transition: background 0.3s ease, color 0.3s ease;

  svg, path {
    max-width: 25px;
    width: 100%;
    fill: ${({ $hasActive }) => ($hasActive ? theme.colors.primary : theme.colors.primary)};
  }

  &:hover {
    background: ${theme.colors.lightGray};
    color: ${theme.colors.primary};

    svg, path {
      fill: ${theme.colors.primary};
    }
  }
`;

const GroupTitle = styled.span<{ $open: boolean }>`
  margin-left: 10px;
  font-size: 0.9em;
  white-space: nowrap;
  flex: 1;
  text-align: left;
`;

const GroupArrow = styled.span<{ $open: boolean }>`
  font-size: 10px;
  transition: transform 0.2s ease;
  transform: ${({ $open }) => ($open ? "rotate(180deg)" : "rotate(0)")};
  margin-right: 5px;
`;

const SubItemsList = styled.div<{ $open: boolean }>`
  overflow: hidden;
  max-height: ${({ $open }) => ($open ? "200px" : "0")};
  transition: max-height 0.3s ease;
`;

const SubItemLink = styled(Link)<{ $selected: boolean }>`
  display: block;
  padding: 8px 10px 8px 45px;
  font-family: ${theme.font.poppins};
  font-size: 0.8em;
  color: ${({ $selected }) => ($selected ? theme.colors.white : theme.colors.blue400)};
  text-decoration: none;
  border-radius: 8px;
  background-color: ${({ $selected }) => ($selected ? theme.colors.primary : "transparent")};
  margin: 2px 10px 2px 0;
  white-space: nowrap;
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: ${theme.colors.lightGray};
    color: ${theme.colors.primary};
  }
`;

// --- Componente de grupo com accordion ---
const NfseGroup: React.FC<{ isMenuOpen: boolean }> = ({ isMenuOpen }) => {
  const [open, setOpen] = React.useState(false);
  const location = useLocation();

  const nfseRoutes = nfseSubItems.map((i) => i.route);
  const hasActiveChild = nfseRoutes.includes(location.pathname);

  // Auto-expand quando uma rota filha está ativa
  React.useEffect(() => {
    if (hasActiveChild) setOpen(true);
  }, [hasActiveChild]);

  if (!isMenuOpen) {
    // Menu colapsado: mostra só o ícone
    return (
      <GroupContainer>
        <GroupHeader
          $open={open}
          $hasActive={hasActiveChild}
          onClick={() => setOpen(!open)}
          title="Configurações NFSe"
        >
          <Icons name="cnae" width={25} height={25} color={theme.colors.primary} />
        </GroupHeader>
      </GroupContainer>
    );
  }

  return (
    <GroupContainer>
      <GroupHeader $open={open} $hasActive={hasActiveChild} onClick={() => setOpen(!open)}>
        <Icons name="cnae" width={25} height={25} color={theme.colors.primary} />
        <GroupTitle $open={open}>Config. NFSe</GroupTitle>
        <GroupArrow $open={open}>▼</GroupArrow>
      </GroupHeader>
      <SubItemsList $open={open}>
        {nfseSubItems.map((sub) => (
          <SubItemLink
            key={sub.key}
            to={sub.route}
            $selected={location.pathname === sub.route}
          >
            {sub.title}
          </SubItemLink>
        ))}
      </SubItemsList>
    </GroupContainer>
  );
};

const MenuAdmin: React.FC = (): JSX.Element => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  const toggleMenu = (): void => setIsMenuOpen(!isMenuOpen);

  return (
    <Menu
      itens={itens}
      isMenuOpen={isMenuOpen}
      toggleMenu={toggleMenu}
      extraContent={<NfseGroup isMenuOpen={isMenuOpen} />}
    />
  );
};
export default MenuAdmin;
